import { v7 as uuidv7 } from 'uuid';

import type {
  EventEnvelopeInput,
  PageViewEventInput,
  RrwebEventInput,
  RrwebDataEventInput,
} from '@mutiny-pkg/analytics-schemas';

export type AnalyticsClientInitOptions = {
  endpoint: string;
  companyId: string;
  assetId: string;
};

const visitorCookieName = 'mutiny_vid';
const rrwebReplayFlushIntervalMs = 5_000;
const rrwebReplayBatchSize = 100;
const rrwebReplayBatchMaxBytes = 60_000;
const keepalivePayloadMaxBytes = 60_000;

type RrwebStopRecording = () => void;
type RrwebRecordOptions = {
  emit: (event: unknown) => void;
  maskAllInputs: true;
  recordCanvas: false;
  collectFonts: false;
  inlineImages: false;
};
type RrwebRecord = ((options: RrwebRecordOptions) => RrwebStopRecording) & {
  mirror: {
    getNode: (id: number) => Node | null;
  };
};
type RrwebModule = {
  record: RrwebRecord;
};

let stopRrwebRecording: RrwebStopRecording | undefined;
let rrwebFlushTimer: number | null = null;
let flushActiveRrwebReplayBatch: (() => void) | null = null;
let removePageLifecycleFlushListeners: Array<() => void> = [];
let rrwebRecordingStartToken = 0;

export function init(options: AnalyticsClientInitOptions): void {
  if (!options.endpoint || !options.companyId || !options.assetId) {
    return;
  }

  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }

  stopActiveRrwebReplayRecording();

  const visitorId = getVisitorId();
  const event: PageViewEventInput = {
    event_id: createId(),
    event_type: 'page_view',
    client_ts: Date.now(),
    properties: {
      url: window.location.href,
      title: document.title,
      referrer: document.referrer,
    },
  };

  sendPayload(options.endpoint, {
    company_id: options.companyId,
    asset_id: options.assetId,
    visitor_id: visitorId,
    events: [event],
  });

  startRrwebReplayRecording(options, visitorId, event.event_id);
}

function createId(): string {
  return uuidv7();
}

function readCookie(name: string): string | null {
  const prefix = `${encodeURIComponent(name)}=`;
  const parts = document.cookie.split(';');
  for (const part of parts) {
    const trimmedPart = part.trim();
    if (trimmedPart.startsWith(prefix)) {
      return decodeURIComponent(trimmedPart.slice(prefix.length));
    }
  }
  return null;
}

function writeCookie(name: string, value: string): void {
  document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}; Max-Age=31536000; Path=/; SameSite=Lax`;
}

function getVisitorId(): string {
  const existingVisitorId = readCookie(visitorCookieName);
  if (existingVisitorId) {
    return existingVisitorId;
  }

  const visitorId = createId();
  writeCookie(visitorCookieName, visitorId);
  return visitorId;
}

function startRrwebReplayRecording(options: AnalyticsClientInitOptions, visitorId: string, pageViewId: string): void {
  stopActiveRrwebReplayRecording();

  let pendingEvents: RrwebEventInput[] = [];
  let pendingBytes = 0;
  const recordingStartToken = ++rrwebRecordingStartToken;

  const flush = (): void => {
    if (pendingEvents.length === 0) {
      return;
    }

    const events = pendingEvents;
    pendingEvents = [];
    pendingBytes = 0;

    const event: RrwebDataEventInput = {
      event_id: createId(),
      event_type: 'rrweb_data',
      client_ts: Date.now(),
      page_view_id: pageViewId,
      properties: {
        events,
      },
    };

    sendPayload(options.endpoint, {
      company_id: options.companyId,
      asset_id: options.assetId,
      visitor_id: visitorId,
      events: [event],
    });
  };

  void import('rrweb')
    .then((rrwebModule: unknown) => {
      if (!isRrwebModule(rrwebModule) || recordingStartToken !== rrwebRecordingStartToken) {
        return;
      }

      const { record } = rrwebModule;
      stopRrwebRecording = record({
        emit(event: unknown) {
          const normalizedEvent = normalizeRrwebEvent(event);
          if (!normalizedEvent) {
            return;
          }

          pendingBytes += JSON.stringify(normalizedEvent).length;
          pendingEvents = [...pendingEvents, normalizedEvent];
          if (pendingEvents.length >= rrwebReplayBatchSize || pendingBytes >= rrwebReplayBatchMaxBytes) {
            flush();
          }
        },
        maskAllInputs: true,
        recordCanvas: false,
        collectFonts: false,
        inlineImages: false,
      });

      flushActiveRrwebReplayBatch = flush;
      if (typeof window.setInterval === 'function') {
        rrwebFlushTimer = window.setInterval(flush, rrwebReplayFlushIntervalMs);
      }
      removePageLifecycleFlushListeners = addPageLifecycleFlushListeners(flush);
    })
    .catch(() => {});
}

function stopActiveRrwebReplayRecording(): void {
  rrwebRecordingStartToken += 1;

  flushActiveRrwebReplayBatch?.();
  flushActiveRrwebReplayBatch = null;

  stopRrwebRecording?.();
  stopRrwebRecording = undefined;

  if (rrwebFlushTimer !== null) {
    window.clearInterval(rrwebFlushTimer);
    rrwebFlushTimer = null;
  }

  for (const removeListener of removePageLifecycleFlushListeners) {
    removeListener();
  }
  removePageLifecycleFlushListeners = [];
}

function isRrwebModule(value: unknown): value is RrwebModule {
  if (!hasProperties(value)) {
    return false;
  }

  const { record } = value;
  if (typeof record !== 'function' || !hasProperties(record)) {
    return false;
  }

  const { mirror } = record;
  if (!hasProperties(mirror)) {
    return false;
  }

  return typeof mirror.getNode === 'function';
}

function hasProperties(value: unknown): value is Record<string, unknown> {
  return (typeof value === 'object' || typeof value === 'function') && value !== null;
}

function addPageLifecycleFlushListeners(flush: () => void): Array<() => void> {
  const removeListeners: Array<() => void> = [];

  const flushWhenHidden = (): void => {
    if (document.visibilityState === 'hidden') {
      flush();
    }
  };

  if (typeof document.addEventListener === 'function') {
    const removeDocumentListener = document.removeEventListener.bind(document);
    document.addEventListener('visibilitychange', flushWhenHidden);
    removeListeners.push(() => {
      removeDocumentListener('visibilitychange', flushWhenHidden);
    });
  }

  if (typeof window.addEventListener === 'function') {
    const removeWindowListener = window.removeEventListener.bind(window);
    window.addEventListener('pagehide', flush);
    window.addEventListener('beforeunload', flush);
    removeListeners.push(() => {
      removeWindowListener('pagehide', flush);
      removeWindowListener('beforeunload', flush);
    });
  }

  return removeListeners;
}

function isRrwebEvent(event: unknown): event is RrwebEventInput {
  if (typeof event !== 'object' || event === null) {
    return false;
  }

  if (!('type' in event) || !isNonnegativeInteger(event.type)) {
    return false;
  }

  if (!('timestamp' in event) || !isNonnegativeInteger(event.timestamp)) {
    return false;
  }

  if ('delay' in event && typeof event.delay !== 'number') {
    return false;
  }

  return 'data' in event && isRrwebJsonValue(event.data);
}

function isNonnegativeInteger(value: unknown): value is number {
  return Number.isInteger(value) && Number(value) >= 0;
}

function normalizeRrwebEvent(event: unknown): RrwebEventInput | null {
  try {
    const serializedEvent = JSON.stringify(event);
    if (!serializedEvent) {
      return null;
    }

    const parsedEvent: unknown = JSON.parse(serializedEvent);
    if (!isRrwebEvent(parsedEvent)) {
      return null;
    }

    return parsedEvent;
  } catch {
    return null;
  }
}

function isRrwebJsonValue(value: unknown): value is RrwebEventInput['data'] {
  if (value === null || typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return true;
  }

  if (Array.isArray(value)) {
    return value.every(isRrwebJsonValue);
  }

  if (typeof value !== 'object') {
    return false;
  }

  return Object.values(value).every(isRrwebJsonValue);
}

function sendPayload(endpoint: string, body: EventEnvelopeInput): void {
  const payload = JSON.stringify(body);
  const canUseKeepalive = new TextEncoder().encode(payload).byteLength <= keepalivePayloadMaxBytes;

  if (
    canUseKeepalive &&
    isSameOriginEndpoint(endpoint) &&
    navigator.sendBeacon?.(endpoint, new Blob([payload], { type: 'application/json' }))
  ) {
    return;
  }

  fetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: payload,
    keepalive: canUseKeepalive,
    credentials: isSameOriginEndpoint(endpoint) ? 'same-origin' : 'omit',
  }).catch(() => {});
}

function isSameOriginEndpoint(endpoint: string): boolean {
  try {
    return new URL(endpoint, window.location.href).origin === window.location.origin;
  } catch {
    return false;
  }
}
